import { Suspense, useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import {
  AppRouterProvider,
  AppSpinner,
  SettingsLayout,
  fetchAuthPublicConfig,
  fetchSession,
  type ProductUser,
} from "@aiuto/components";

import { ProductShell } from "./shell/product-shell";
import {
  CanonicalCollectionRoutePage,
  InboxRoutePage,
  InstallRoute,
  LibraryRoutePage,
  PersonalCollectionRoutePage,
  RejectedRoutePage,
  SettingsAccountRoutePage,
  SettingsAirtableRoutePage,
  SettingsArchivedRoutePage,
  SettingsIntegrationsRoutePage,
  SettingsMiroRoutePage,
  SettingsPromptsRoutePage,
  SettingsSlackRoutePage,
  SettingsUsersRoutePage,
  SettingsWorkOSRoutePage,
  SignInRoute,
  TalentRoutePage,
  TeamCollectionRoutePage,
  TeamMemberRoutePage,
  TeamRoutePage,
} from "./routes/lazy";

const RouteFallback = () => null;

const RequireAuth = ({
  user,
  children,
}: {
  user: ProductUser | null;
  children: ReactNode;
}) => {
  const location = useLocation();
  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <>{children}</>;
};

const RequireCurator = ({ user, children }: { user: ProductUser; children: ReactNode }) => {
  if (user.role !== "curator") return <Navigate to="/library" replace />;
  return <>{children}</>;
};

const AuthenticatedRoutes = ({ user }: { user: ProductUser }) => (
  <ProductShell currentUser={user}>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/library" element={<LibraryRoutePage />} />
        <Route
          path="/inbox"
          element={
            <RequireCurator user={user}>
              <InboxRoutePage />
            </RequireCurator>
          }
        />
        <Route
          path="/rejected"
          element={
            <RequireCurator user={user}>
              <RejectedRoutePage />
            </RequireCurator>
          }
        />
        <Route path="/collections/canonical" element={<CanonicalCollectionRoutePage />} />
        <Route path="/collections/mine" element={<PersonalCollectionRoutePage />} />
        <Route path="/team" element={<TeamRoutePage />} />
        <Route path="/team/member" element={<TeamMemberRoutePage />} />
        <Route path="/team/collection" element={<TeamCollectionRoutePage />} />
        <Route
          path="/talent"
          element={
            <RequireCurator user={user}>
              <TalentRoutePage />
            </RequireCurator>
          }
        />
        <Route
          path="/settings/*"
          element={
            <SettingsLayout>
              <Suspense fallback={<RouteFallback />}>
                <Outlet />
              </Suspense>
            </SettingsLayout>
          }
        >
          <Route path="account" element={<SettingsAccountRoutePage />} />
          <Route path="users" element={<SettingsUsersRoutePage />} />
          <Route path="prompts" element={<SettingsPromptsRoutePage />} />
          <Route path="archived" element={<SettingsArchivedRoutePage />} />
          <Route path="integrations" element={<SettingsIntegrationsRoutePage />} />
          <Route path="integrations/slack" element={<SettingsSlackRoutePage />} />
          <Route path="integrations/miro" element={<SettingsMiroRoutePage />} />
          <Route path="integrations/workos" element={<SettingsWorkOSRoutePage />} />
          <Route path="integrations/airtable" element={<SettingsAirtableRoutePage />} />
          <Route path="*" element={<Navigate to="/settings/account" replace />} />
        </Route>
        <Route path="/" element={<Navigate to="/library" replace />} />
        <Route path="*" element={<Navigate to="/library" replace />} />
      </Routes>
    </Suspense>
  </ProductShell>
);

const AppRoutes = () => {
  const location = useLocation();
  const [user, setUser] = useState<ProductUser | null | undefined>(undefined);
  const [hasUsers, setHasUsers] = useState(true);

  useEffect(() => {
    void (async () => {
      const t0 = Date.now();
      const [config, session] = await Promise.all([
        fetchAuthPublicConfig(),
        fetchSession(),
      ]);
      const configMs = Date.now() - t0;
      setHasUsers(config.hasUsers);
      if (!config.hasUsers && !location.pathname.startsWith("/install")) {
        setUser(null);
        return;
      }
      // #region agent log
      fetch("http://127.0.0.1:7452/ingest/1107ff22-7c75-4195-851d-c6d92208f85e", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "0e474e" },
        body: JSON.stringify({
          sessionId: "0e474e",
          runId: "post-fix",
          hypothesisId: "C,E",
          location: "App.tsx:authBootstrap",
          message: "app auth bootstrap complete",
          data: {
            pathname: location.pathname,
            totalMs: Date.now() - t0,
            configMs,
            hasUser: Boolean(session?.ok),
            parallel: true,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      setUser(session?.ok ? session.data : null);
    })();
  }, [location.pathname]);

  if (user === undefined) {
    return (
      <AppSpinner
        data-testid="app-auth-loading"
        label="Loading…"
        size="lg"
        className="app-spinner--centered"
      />
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/install" element={hasUsers ? <Navigate to="/sign-in" replace /> : <InstallRoute />} />
        <Route
          path="/sign-in"
          element={user ? <Navigate to="/library" replace /> : <SignInRoute />}
        />
        <Route
          path="/*"
          element={
            <RequireAuth user={user}>
              {user ? <AuthenticatedRoutes user={user} /> : null}
            </RequireAuth>
          }
        />
      </Routes>
    </Suspense>
  );
};

export const App = () => (
  <AppRouterProvider>
    <AppRoutes />
  </AppRouterProvider>
);
